import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import styles from './ToastContext.module.scss';
import { registerStoreToast } from '@utils/storeToast';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface ToastOptions {
  variant?: 'cart' | 'wishlist';
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  variant?: 'cart' | 'wishlist';
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType, duration?: number, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearDismissTimer = useCallback(() => {
    if (dismissTimeoutRef.current !== null) {
      clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = 'info', duration: number = 3000, options?: ToastOptions) => {
      clearDismissTimer();
      const id = Math.random().toString(36).substring(7);
      const newToast: Toast = {
        id,
        message,
        type,
        duration,
        variant: options?.variant,
      };
      setToasts([newToast]);
      dismissTimeoutRef.current = setTimeout(() => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
        dismissTimeoutRef.current = null;
      }, duration);
    },
    [clearDismissTimer]
  );

  useEffect(() => {
    registerStoreToast(showToast);
    return () => {
      registerStoreToast(() => {});
      clearDismissTimer();
    };
  }, [showToast, clearDismissTimer]);

  const removeToast = useCallback(
    (id: string) => {
      clearDismissTimer();
      setToasts((prev) => prev.filter((toast) => toast.id !== id));
    },
    [clearDismissTimer]
  );

  const getToastIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M16.6667 5L7.50004 14.1667L3.33337 10"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
        );
      case 'error':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M12.5 7.5L7.5 12.5M7.5 7.5L12.5 12.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        );
      case 'warning':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10 6.66667V10M10 13.3333H10.0083M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        );
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M10 6.66667V10M10 13.3333H10.0083"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        );
    }
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className={styles.toastContainer}>
        {toasts.map((toast) => {
          const isRich = toast.variant === 'cart' || toast.variant === 'wishlist';
          return (
            <div
              key={toast.id}
              className={`${styles.toast} ${isRich ? styles.toastRich : ''} ${isRich && toast.variant ? styles[`toastRich${toast.variant.charAt(0).toUpperCase() + toast.variant.slice(1)}`] : ''} ${!isRich ? styles[`toast${toast.type.charAt(0).toUpperCase() + toast.type.slice(1)}`] : ''}`}
              onClick={() => !isRich && removeToast(toast.id)}
            >
              {isRich ? (
                <>
                  <div className={styles.richCheckWrap}>
                    <div className={styles.richCheck}>
                      <i className="fas fa-check" />
                    </div>
                  </div>
                  <div className={styles.richBody}>
                    <div className={styles.richTitle}>{toast.message}</div>
                  </div>
                  <button
                    className={styles.toastClose}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeToast(toast.id);
                    }}
                    aria-label="Close"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                </>
              ) : (
                <>
                  <div className={styles.toastIcon}>{getToastIcon(toast.type)}</div>
                  <div className={styles.toastContent}>
                    <div className={styles.toastMessage}>{toast.message}</div>
                  </div>
                  <button
                    className={styles.toastClose}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeToast(toast.id);
                    }}
                    aria-label="Close"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}; 